/**
 * GIF Settings Panel
 * Admin configuration for Tenor GIF API key
 */

import { useState } from 'react';
import { Eye, EyeOff, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/services/api.client';
import type { ApiResponse } from '@/types/api.types';
import { toast } from 'sonner';
import { asApiError } from '@/utils/error.util';

interface GifSettings {
  hasApiKey: boolean;
  apiKey: string | null;
  provider: string;
}

const useGifSettings = () => {
  return useQuery({
    queryKey: ['gif-settings'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<GifSettings>>('/settings/gif');
      if (!response.data.data) throw new Error('Failed to fetch GIF settings');
      return response.data.data;
    },
    staleTime: 30 * 1000,
  });
};

const useUpdateGifSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (apiKey: string) => {
      await apiClient.put('/settings/gif', { apiKey });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gif-settings'] });
    },
  });
};

export const GifSettingsPanel = () => {
  const { data: settings, isLoading } = useGifSettings();
  const updateMutation = useUpdateGifSettings();

  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await apiClient.post<ApiResponse<{ success: boolean; message: string }>>('/settings/gif/test');
      const data = response.data.data!;
      setTestResult({ success: data.success, message: data.message });
    } catch (err) {
      const message = asApiError(err).response?.data?.message || asApiError(err).message || 'Неуспешно свързване';
      setTestResult({ success: false, message });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error('Моля, въведете API ключ');
      return;
    }

    try {
      await updateMutation.mutateAsync(apiKey);
      toast.success('GIF API ключът е запазен');
      setApiKey('');
    } catch (error) {
      toast.error(asApiError(error).message || 'Грешка при запазване');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500 dark:text-dark-text-muted">
        Конфигурирайте Tenor API ключ за GIF търсачката в чата. Ключът е безплатен от Google Cloud Console.
      </p>

      {/* Status */}
      <div className="flex items-center gap-2">
        {settings?.hasApiKey ? (
          <>
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm text-green-600 dark:text-green-400">API ключ е конфигуриран</span>
          </>
        ) : (
          <>
            <XCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm text-red-600 dark:text-red-400">API ключ не е конфигуриран</span>
          </>
        )}
      </div>

      {/* API Key Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
          Tenor API ключ
        </label>
        <div className="relative">
          <input
            type={showKey ? 'text' : 'password'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={settings?.hasApiKey ? '••••••••••••••••' : 'AIza...'}
            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={() => setShowKey(!showKey)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-400 dark:text-dark-text-muted">
          Вземете безплатен ключ от{' '}
          <span className="text-blue-500">console.cloud.google.com</span>
          {' '}→ APIs & Services → Tenor API
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={!apiKey.trim() || updateMutation.isPending}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
        >
          {updateMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Запази
        </button>
        <button
          onClick={handleTestConnection}
          disabled={testing || !settings?.hasApiKey}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
        >
          {testing && <Loader2 className="w-4 h-4 animate-spin" />}
          Тест връзка
        </button>
      </div>

      {/* Test result */}
      {testResult && (
        <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
          testResult.success
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
        }`}>
          {testResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {testResult.message}
        </div>
      )}

      {/* Info */}
      <div className="mt-8 p-4 bg-gray-50 dark:bg-dark-bg-hover rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
          Как да получите API ключ
        </h3>
        <ol className="space-y-1 text-sm text-gray-500 dark:text-dark-text-muted list-decimal list-inside">
          <li>Отворете Google Cloud Console</li>
          <li>Създайте или изберете проект</li>
          <li>Активирайте "Tenor API" от APIs & Services</li>
          <li>Създайте API ключ от Credentials</li>
        </ol>
        <p className="mt-2 text-xs text-gray-400 dark:text-dark-text-muted">
          Tenor API е напълно безплатен, без лимити за малки екипи.
        </p>
      </div>
    </div>
  );
};
